import { ReactRenderer } from '@tiptap/react'
import type { SuggestionOptions, SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion'
import { MentionList, type MentionListRef, type MentionUser } from './mention-list'

export function createMentionSuggestion(
  getUsers: () => MentionUser[],
): Omit<SuggestionOptions<MentionUser>, 'editor'> {
  return {
    items: ({ query }) => {
      const q = query.toLowerCase()
      return getUsers()
        .filter(
          (u) =>
            u.firstName.toLowerCase().includes(q) ||
            u.lastName.toLowerCase().includes(q) ||
            `${u.firstName} ${u.lastName}`.toLowerCase().includes(q),
        )
        .slice(0, 5)
    },

    render: () => {
      let component: ReactRenderer<MentionListRef> | null = null
      let popup: HTMLDivElement | null = null

      return {
        onStart: (props: SuggestionProps<MentionUser>) => {
          component = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
          })

          popup = document.createElement('div')
          popup.style.position = 'absolute'
          popup.style.zIndex = '50'
          document.body.appendChild(popup)

          if (component.element) {
            popup.appendChild(component.element)
          }

          updatePosition(popup, props)
        },

        onUpdate: (props: SuggestionProps<MentionUser>) => {
          component?.updateProps(props)
          if (popup) {
            updatePosition(popup, props)
          }
        },

        onKeyDown: (props: SuggestionKeyDownProps) => {
          // Leave Escape to the suggestion plugin: it calls onExit (which
          // removes the popup) and also ends the suggestion, so the next
          // Escape reaches the editor instead of an invisible popup.
          if (props.event.key === 'Escape') return false
          return component?.ref?.onKeyDown(props) ?? false
        },

        onExit: () => {
          popup?.remove()
          component?.destroy()
          popup = null
          component = null
        },
      }
    },
  }
}

function updatePosition(
  popup: HTMLDivElement,
  props: SuggestionProps<MentionUser>,
) {
  const rect = props.clientRect?.()
  if (!rect) return

  popup.style.left = `${rect.left + window.scrollX}px`
  popup.style.top = `${rect.bottom + window.scrollY + 4}px`
}
